import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import Modal from './Modal';
import './DashboardOptionsEditor.css';

// Monaco Editor를 lazy import로 변경
const Editor = lazy(() => import('@monaco-editor/react'));

/**
 * 대시보드 옵션 편집 모달 컴포넌트
 * 
 * @param {Object} props
 * @param {Object} props.dashboard - 현재 대시보드 템플릿 객체
 * @param {Function} props.onSave - 저장 시 호출될 콜백 함수
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 닫기 버튼 클릭 시 호출될 콜백 함수
 */
const DashboardOptionsEditor = ({ dashboard, onSave, open, onClose, dashboardId, onPendingBackgroundChange }) => {
  // 기본 옵션 값 (대시보드에 대시보드 옵션이 없는 경우 사용)
  const defaultOptions = {
    background_color: '#f5f5f5',
    grid_spacing: 10,
    padding: { top: 20, right: 20, bottom: 20, left: 20 },
    header: {
      show: true,
      height: 60,
      background_color: '#ffffff',
      text_color: '#333333'
    },
    description: '',
    layout_mode: 'vertical'  // 레이아웃 밀기 모드 기본값 (vertical, horizontal, none)
  };

  // 현재 활성 탭 상태
  const [activeTab, setActiveTab] = useState('general');
  
  // 템플릿 편집 상태
  const [editedTemplate, setEditedTemplate] = useState('');
  const [templateError, setTemplateError] = useState(null);
  const editorRef = useRef(null);
  
  // 편집 중인 옵션 상태
  const [options, setOptions] = useState(dashboard.dashboard_options || defaultOptions);
  
  // 제목 상태
  const [title, setTitle] = useState(dashboard.title || '');
  
  // 대시보드 목록 숨김 상태
  const [isHiddenInList, setIsHiddenInList] = useState(dashboard.is_hidden_in_list || false);
  
  // 공통 대시보드 여부
  const [isCommonDashboard, setIsCommonDashboard] = useState(
    dashboard.is_common_dashboard !== undefined 
      ? dashboard.is_common_dashboard 
      : (dashboard.user === null || dashboard.user_id === null)
  );
  
  // 레이아웃 그리드 세밀도 상태 (정사각형 그리드: 컬럼 수)
  const [columns, setColumns] = useState(dashboard.layout?.columns || 12);

  // 배경 설정 상태 (DashboardBackgroundModal과 동일한 구조 차용)
  const [bgPreviewUrl, setBgPreviewUrl] = useState(dashboard?.background_image_url || null);
  const [bgOpacity, setBgOpacity] = useState(dashboard?.background_opacity ?? 1.0);
  const [bgPosition, setBgPosition] = useState(dashboard?.background_position || 'center');
  const [bgSize, setBgSize] = useState(dashboard?.background_size || 'cover');
  const [bgRemoveFlag, setBgRemoveFlag] = useState(false);
  const bgFileInputRef = useRef(null);

  // 마우스 다운 이벤트가 backdrop에서 발생했는지 추적하는 ref
  const mouseDownOnBackdrop = useRef(false);

  // 대시보드 변경 시 상태 초기화
  useEffect(() => {
    if (open) {
      console.log('[DashboardOptionsEditor] 모달 열림 - dashboard:', {
        is_common_dashboard: dashboard.is_common_dashboard,
        user: dashboard.user,
        user_id: dashboard.user_id,
        title: dashboard.title
      });
      
      // 기본값과 기존 옵션을 병합하여 안전하게 초기화
      const safeOptions = {
        ...defaultOptions,
        ...(dashboard.dashboard_options || {}),
        // padding 객체는 개별적으로 병합
        padding: {
          ...defaultOptions.padding,
          ...(dashboard.dashboard_options?.padding || {})
        },
        // header 객체도 개별적으로 병합
        header: {
          ...defaultOptions.header,
          ...(dashboard.dashboard_options?.header || {})
        }
      };
      
      setOptions(safeOptions);
      setTitle(dashboard.title || '');
      setColumns(dashboard.layout?.columns || 12);
      setIsHiddenInList(dashboard.is_hidden_in_list || false);
      // is_common_dashboard 필드가 있으면 그것을 사용, 없으면 user/user_id로 판단
      if (dashboard.is_common_dashboard !== undefined) {
        console.log('[DashboardOptionsEditor] is_common_dashboard 사용:', dashboard.is_common_dashboard);
        setIsCommonDashboard(dashboard.is_common_dashboard);
      } else {
        const isCommon = dashboard.user === null || dashboard.user_id === null;
        console.log('[DashboardOptionsEditor] user/user_id로 판단:', isCommon);
        setIsCommonDashboard(isCommon);
      }

      // 배경 초기화
      setBgPreviewUrl(dashboard?.background_image_url || null);
      setBgOpacity(dashboard?.background_opacity ?? 1.0);
      setBgPosition(dashboard?.background_position || 'center');
      setBgSize(dashboard?.background_size || 'cover');
      setBgRemoveFlag(false);
      
      // 템플릿 초기화
      try {
        const templateToEdit = JSON.stringify(dashboard, null, 2);
        setEditedTemplate(templateToEdit);
      } catch (e) {
        console.error('[DashboardOptionsEditor] 템플릿 초기화 실패:', e);
        setEditedTemplate('{}');
      }
    }
  }, [dashboard, open]);

  // 일반 옵션 변경 핸들러
  const handleOptionChange = (option, value) => {
    setOptions(prevOptions => ({
      ...prevOptions,
      [option]: value
    }));
  };

  // 중첩된 옵션 변경 핸들러
  const handleNestedOptionChange = (parent, option, value) => {
    setOptions(prevOptions => ({
      ...prevOptions,
      [parent]: {
        ...prevOptions[parent],
        [option]: value
      }
    }));
  };

  // 패딩 변경 핸들러
  const handlePaddingChange = (side, value) => {
    setOptions(prevOptions => ({
      ...prevOptions,
      padding: {
        ...defaultOptions.padding, // 기본값 먼저 설정
        ...(prevOptions.padding || {}), // 기존 값이 있으면 병합
        [side]: parseInt(value, 10) || 0
      }
    }));
  };

  // 적용 핸들러
  const handleApply = () => {
    // 템플릿 탭에서 편집한 경우
    if (activeTab === 'template') {
      try {
        const parsedTemplate = JSON.parse(editedTemplate);
        // 상단 background_*를 dashboard_options로 자동 매핑
        if (parsedTemplate && typeof parsedTemplate === 'object') {
          const topBgUrl = parsedTemplate.background_image_url;
          const topBgOpacity = parsedTemplate.background_opacity;
          const topBgPosition = parsedTemplate.background_position;
          const topBgSize = parsedTemplate.background_size;
          const hasAnyTopBg = typeof topBgUrl !== 'undefined' || typeof topBgOpacity !== 'undefined' || typeof topBgPosition !== 'undefined' || typeof topBgSize !== 'undefined';
          if (hasAnyTopBg) {
            parsedTemplate.dashboard_options = parsedTemplate.dashboard_options || {};
            if (typeof topBgUrl !== 'undefined' && (parsedTemplate.dashboard_options.background_image_url === undefined)) {
              parsedTemplate.dashboard_options.background_image_url = topBgUrl;
            }
            if (typeof topBgOpacity !== 'undefined' && (parsedTemplate.dashboard_options.background_opacity === undefined)) {
              parsedTemplate.dashboard_options.background_opacity = topBgOpacity;
            }
            if (typeof topBgPosition !== 'undefined' && (parsedTemplate.dashboard_options.background_position === undefined)) {
              parsedTemplate.dashboard_options.background_position = topBgPosition;
            }
            if (typeof topBgSize !== 'undefined' && (parsedTemplate.dashboard_options.background_size === undefined)) {
              parsedTemplate.dashboard_options.background_size = topBgSize;
            }
          }
        }
        // dashboard_id 변경 감지
        const originalDashboardId = dashboardId || dashboard?.dashboard_id || dashboard?.dashboardId;
        const newDashboardId = parsedTemplate.dashboard_id || parsedTemplate.dashboardId;
        const dashboardIdChanged = originalDashboardId !== newDashboardId;
        
        // changeInfo 매개변수 추가
        onSave(parsedTemplate, { dashboardIdChanged, originalDashboardId, newDashboardId });
        onClose();
      } catch (e) {
        setTemplateError('템플릿을 적용할 수 없습니다. JSON 형식을 확인해주세요.');
        return;
      }
    } else {
      // 기존 옵션 탭들에서 편집한 경우 (부분 옵션 수정 payload만 전달)
      const newColumns = parseInt(columns, 10);
      const oldColumns = dashboard.layout?.columns || 12;

      // 자동 비례 조정 여부 확인 팝업 (컬럼 수 변경 시)
      let autoResizeFlags = {};
      if (newColumns !== oldColumns) {
        const confirmed = window.confirm(
          `그리드를 ${oldColumns}열에서 ${newColumns}열로 변경합니다.\n기존 차트 크기를 자동으로 비례 조정할까요?`
        );
        if (confirmed) {
          autoResizeFlags = {
            _autoResizeNeeded: true,
            _resizeRatio: newColumns / oldColumns
          };
        }
      }

      const updatedDashboard = {
        title,
        is_hidden_in_list: isHiddenInList,
        is_common_dashboard: isCommonDashboard,
        layout: {
          ...dashboard.layout,
          columns: newColumns
        },
        dashboard_options: options,
        ...autoResizeFlags
      };

      // 배경 설정 변경 사항을 부모로 전달 (서버 커밋은 저장 시점)
      if (typeof onPendingBackgroundChange === 'function') {
        const file = bgFileInputRef.current?.files?.[0] || null;
        const bgSettingsChanged = (
          bgOpacity !== (dashboard?.background_opacity ?? 1.0) ||
          bgPosition !== (dashboard?.background_position || 'center') ||
          bgSize !== (dashboard?.background_size || 'cover')
        );
        if (bgRemoveFlag) {
          onPendingBackgroundChange({ removeFlag: true });
        } else if (file || bgSettingsChanged) {
          onPendingBackgroundChange({
            file: file || null,
            previewUrl: bgPreviewUrl || null,
            background_opacity: bgOpacity,
            background_position: bgPosition,
            background_size: bgSize,
            imageChanged: !!file,
            settingsChanged: bgSettingsChanged
          });
        }
      }
      onSave(updatedDashboard);
      onClose();
    }
  };

  // 배경 클릭 처리 함수 - 마우스 다운과 마우스 업이 모두 배경에서 발생했을 때만 모달을 닫음
  const handleBackdropMouseDown = (e) => {
    // 모달 컨텐츠 영역이 아닌 배경 영역에서만 처리
    if (e.target.className === 'dashboard-options-editor-backdrop') {
      mouseDownOnBackdrop.current = true;
    } else {
      mouseDownOnBackdrop.current = false;
    }
  };

  const handleBackdropMouseUp = (e) => {
    // 마우스 다운이 배경에서 발생했고, 마우스 업도 배경에서 발생한 경우에만 모달 닫기
    if (mouseDownOnBackdrop.current && e.target.className === 'dashboard-options-editor-backdrop') {
      onClose();
    }
    // 상태 초기화
    mouseDownOnBackdrop.current = false;
  };

  return (
    <Modal isOpen={open} onClose={onClose}>
      <div 
        className="dashboard-options-editor-backdrop" 
        onMouseDown={handleBackdropMouseDown} 
        onMouseUp={handleBackdropMouseUp}
      >
        <div className="dashboard-options-editor-content" onClick={e => e.stopPropagation()}>
        <div className="dashboard-options-editor-header">
          <h3>대시보드 옵션 편집</h3>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="dashboard-options-editor-tabs">
          <button 
            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            기본 정보
          </button>
          <button 
            className={`tab-button ${activeTab === 'style' ? 'active' : ''}`}
            onClick={() => setActiveTab('style')}
          >
            스타일
          </button>
          {/* 헤더 탭 - 숨김 처리 */}
          {/* <button
            className={`tab-button ${activeTab === 'header' ? 'active' : ''}`}
            onClick={() => setActiveTab('header')}
          >
            헤더
          </button> */}
          {/* 템플릿 탭 - 숨김 처리 */}
          {/* <button
            className={`tab-button ${activeTab === 'template' ? 'active' : ''}`}
            onClick={() => setActiveTab('template')}
          >
            템플릿
          </button> */}
        </div>

        <div className="dashboard-options-editor-body">
          {/* 기본 정보 탭 */}
          {activeTab === 'general' && (
            <div className="options-section">
              <div className="form-group">
                <label>대시보드 제목</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    if (e.target.value.length <= 50) setTitle(e.target.value);
                  }}
                  placeholder="대시보드 제목을 입력하세요"
                  maxLength={50}
                />
                <small className="form-help-text">
                  한글, 영문, 숫자, 공백, 특수문자(- _ / ( ) [ ]) 사용 가능 (최대 50자, 현재 {title.length}자)
                </small>
              </div>
              
              {/* 그리드 세밀도: 바탕의 UX를 이곳으로 이동 */}
              <div className="form-group">
                <label>그리드 세밀도</label>
                <select
                  value={columns}
                  onChange={(e) => setColumns(parseInt(e.target.value, 10))}
                >
                  <option value={12}>기본 (12열)</option>
                  <option value={24}>세밀 (24열)</option>
                  <option value={48}>매우 세밀 (48열)</option>
                  <option value={96}>초정밀 (96열)</option>
                  <option value={192}>극초정밀 (192열)</option>
                </select>
                <small className="form-help-text">
                  변경 시 기존 차트가 새로운 그리드에 맞춰 자동으로 비례 조정됩니다.
                </small>
              </div>
              
              <div className="form-group">
                <label>대시보드 설명</label>
                <textarea
                  value={options.description}
                  onChange={(e) => {
                    if (e.target.value.length <= 200) handleOptionChange('description', e.target.value);
                  }}
                  placeholder="대시보드에 대한 설명을 입력하세요"
                  maxLength={200}
                  rows={3}
                />
                <small className="form-help-text">
                  한글, 영문, 숫자, 공백, 특수문자(- _ / ( ) [ ]) 사용 가능 (최대 200자, 현재 {(options.description || '').length}자)
                </small>
              </div>
              
              <div className="form-group">
                <label>대시보드 목록에서 숨기기</label>
                <div className="switch-container">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isHiddenInList}
                      onChange={(e) => setIsHiddenInList(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span>{isHiddenInList ? '숨김 (편집 모드에서만 표시)' : '표시'}</span>
                </div>
                <small className="form-help-text">
                  팝업용 서브 대시보드 등 메인 목록에 표시하지 않을 대시보드에 사용합니다.
                </small>
              </div>
              
              {/* 대시보드 유형 - 숨김 처리 */}
              {/* <div className="form-group">
                <label>대시보드 유형</label>
                <div className="switch-container">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isCommonDashboard}
                      onChange={(e) => setIsCommonDashboard(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span>{isCommonDashboard ? '🌐 공통 대시보드 (모든 사용자 공유)' : '👤 개인 대시보드 (나만 사용)'}</span>
                </div>
                <small className="form-help-text">
                  공통 대시보드는 같은 테넌트의 모든 사용자가 볼 수 있습니다. 개인 대시보드는 나만 볼 수 있습니다.
                </small>
              </div> */}
            </div>
          )}

          {/* 스타일 탭 */}
          {activeTab === 'style' && (
            <div className="options-section">
              <div className="form-group">
                <label>배경 유형</label>
                <select
                  value={options.background?.mode || (options.background_color ? 'solid' : 'solid')}
                  onChange={(e) => {
                    const mode = e.target.value;
                    setOptions(prev => ({
                      ...prev,
                      background: {
                        ...(prev.background || {}),
                        mode,
                        // 기본값 채우기
                        solid: prev.background?.solid || { color: prev.background_color || '#f5f5f5' },
                        gradient: prev.background?.gradient || {
                          angleDeg: 135,
                          stops: [
                            { color: '#f0f8ff', positionPercent: 0 },
                            { color: '#c8e4f1', positionPercent: 100 }
                          ]
                        }
                      }
                    }));
                  }}
                >
                  <option value="solid">단색</option>
                  <option value="gradient">그라데이션</option>
                </select>
              </div>

              {/* 단색 설정 */}
              { (options.background?.mode || (options.background_color ? 'solid' : 'solid')) === 'solid' && (
                <div className="form-group">
                  <label>배경색</label>
                  <div className="color-input-container">
                    <input
                      type="color"
                      value={options.background?.solid?.color || options.background_color || '#f5f5f5'}
                      onChange={(e) => {
                        const color = e.target.value;
                        setOptions(prev => ({
                          ...prev,
                          background_color: color, // 하위호환
                          background: {
                            ...(prev.background || {}),
                            mode: 'solid',
                            solid: { color }
                          }
                        }));
                      }}
                    />
                    <input
                      type="text"
                      value={options.background?.solid?.color || options.background_color || '#f5f5f5'}
                      onChange={(e) => {
                        const color = e.target.value;
                        if (/^#[0-9A-Fa-f]{0,6}$/.test(color) || color === '') {
                          setOptions(prev => ({
                            ...prev,
                            background_color: color,
                            background: {
                              ...(prev.background || {}),
                              mode: 'solid',
                              solid: { color }
                            }
                          }));
                        }
                      }}
                      placeholder="#f5f5f5"
                      maxLength={7}
                    />
                  </div>
                </div>
              )}

              {/* 그라데이션 설정 */}
              { (options.background?.mode) === 'gradient' && (
                <>
                  <div className="form-group">
                    <label>각도(도)</label>
                    <input
                      type="number"
                      min="0"
                      max="360"
                      value={options.background?.gradient?.angleDeg ?? 135}
                      onChange={(e) => {
                        const angleDeg = parseInt(e.target.value || '0', 10);
                        setOptions(prev => ({
                          ...prev,
                          background: {
                            ...(prev.background || {}),
                            mode: 'gradient',
                            gradient: {
                              ...(prev.background?.gradient || {}),
                              angleDeg
                            }
                          }
                        }));
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>그라데이션 색상</label>
                    <div className="color-input-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {(() => {
                        const stops = options.background?.gradient?.stops || [
                          { color: '#f0f8ff', positionPercent: 0 },
                          { color: '#c8e4f1', positionPercent: 100 }
                        ];
                        return stops.map((stop, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <input
                              type="color"
                              value={stop.color}
                              onChange={(e) => {
                                const color = e.target.value;
                                setOptions(prev => {
                                  const prevStops = (prev.background?.gradient?.stops || []).slice();
                                  prevStops[idx] = { ...(prevStops[idx] || {}), color };
                                  return {
                                    ...prev,
                                    background: {
                                      ...(prev.background || {}),
                                      mode: 'gradient',
                                      gradient: { ...(prev.background?.gradient || {}), stops: prevStops }
                                    }
                                  };
                                });
                              }}
                            />
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={stop.positionPercent ?? 0}
                              onChange={(e) => {
                                const positionPercent = parseInt(e.target.value || '0', 10);
                                setOptions(prev => {
                                  const prevStops = (prev.background?.gradient?.stops || []).slice();
                                  prevStops[idx] = { ...(prevStops[idx] || {}), positionPercent };
                                  return {
                                    ...prev,
                                    background: {
                                      ...(prev.background || {}),
                                      mode: 'gradient',
                                      gradient: { ...(prev.background?.gradient || {}), stops: prevStops }
                                    }
                                  };
                                });
                              }}
                            />
                            <span>%</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </>
              )}

              {/* 테마 항목 제거 */}
              
              <div className="form-group">
                <label>차트간 간격 (spacing)</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={options.grid_spacing}
                  onChange={(e) => handleOptionChange('grid_spacing', parseInt(e.target.value, 10))}
                />
                <small className="form-help-text">
                  0으로 설정하면 그리드 정렬이 해제되어 자유롭게 이동할 수 있습니다.
                </small>
              </div>
              
              {/* 패딩 - 숨김 처리 */}
              {/* <div className="form-group">
                <label>패딩</label>
                <div className="padding-inputs">
                  <div className="padding-input">
                    <label>상단</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={options.padding?.top || 0}
                      onChange={(e) => handlePaddingChange('top', e.target.value)}
                    />
                  </div>
                  <div className="padding-input">
                    <label>우측</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={options.padding?.right || 0}
                      onChange={(e) => handlePaddingChange('right', e.target.value)}
                    />
                  </div>
                  <div className="padding-input">
                    <label>하단</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={options.padding?.bottom || 0}
                      onChange={(e) => handlePaddingChange('bottom', e.target.value)}
                    />
                  </div>
                  <div className="padding-input">
                    <label>좌측</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={options.padding?.left || 0}
                      onChange={(e) => handlePaddingChange('left', e.target.value)}
                    />
                  </div>
                </div>
              </div> */}

              {/* 배경 설정 섹션 - 숨김 처리 */}
              {/* <div className="form-group">
                <label>배경 설정</label>
                <div className="background-settings">
                  <div className="background-upload">
                    <input
                      ref={bgFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => setBgPreviewUrl(reader.result);
                          reader.readAsDataURL(file);
                          setBgRemoveFlag(false);
                        }
                      }}
                      className="file-input"
                    />
                    <div className="upload-buttons">
                      <button
                        type="button"
                        className="file-select-btn"
                        onClick={() => bgFileInputRef.current?.click()}
                      >
                        {bgPreviewUrl ? '이미지 변경' : '이미지 선택'}
                      </button>
                      {bgPreviewUrl && (
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => { setBgPreviewUrl(null); setBgRemoveFlag(true); }}
                        >
                          배경 제거
                        </button>
                      )}
                    </div>
                  </div>

                  {bgPreviewUrl && (
                    <div className="preview-section">
                      <div
                        className="preview-container"
                        style={{
                          backgroundImage: `url(${bgPreviewUrl})`,
                          backgroundSize: bgSize,
                          backgroundPosition: bgPosition,
                          opacity: bgOpacity
                        }}
                      >
                        <div className="preview-content">미리보기</div>
                      </div>
                    </div>
                  )}

                  <div className="settings-section">
                    <div className="setting-item">
                      <label>투명도:</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={bgOpacity}
                        onChange={(e) => setBgOpacity(parseFloat(e.target.value))}
                      />
                      <span>{bgOpacity}</span>
                    </div>
                    <div className="setting-item">
                      <label>위치:</label>
                      <select value={bgPosition} onChange={(e) => setBgPosition(e.target.value)}>
                        <option value="center">가운데</option>
                        <option value="top">상단</option>
                        <option value="bottom">하단</option>
                        <option value="left">왼쪽</option>
                        <option value="right">오른쪽</option>
                        <option value="top left">상단 왼쪽</option>
                        <option value="top right">상단 오른쪽</option>
                        <option value="bottom left">하단 왼쪽</option>
                        <option value="bottom right">하단 오른쪽</option>
                      </select>
                    </div>
                    <div className="setting-item">
                      <label>크기:</label>
                      <select value={bgSize} onChange={(e) => setBgSize(e.target.value)}>
                        <option value="cover">화면 채우기 (cover)</option>
                        <option value="contain">전체 보기 (contain)</option>
                        <option value="auto">원본 크기 (auto)</option>
                        <option value="100% 100%">늘이기 (stretch)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          )}

          {/* 헤더 탭 */}
          {activeTab === 'header' && (
            <div className="options-section">
              <div className="form-group">
                <label>헤더 표시</label>
                <div className="switch-container">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={options.header.show}
                      onChange={(e) => handleNestedOptionChange('header', 'show', e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span>{options.header.show ? '표시' : '숨김'}</span>
                </div>
              </div>
              
              {options.header.show && (
                <>
                  <div className="form-group">
                    <label>헤더 높이</label>
                    <input
                      type="number"
                      min="30"
                      max="200"
                      value={options.header.height}
                      onChange={(e) => handleNestedOptionChange('header', 'height', parseInt(e.target.value, 10))}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>헤더 배경색</label>
                    <div className="color-input-container">
                      <input
                        type="color"
                        value={options.header.background_color}
                        onChange={(e) => handleNestedOptionChange('header', 'background_color', e.target.value)}
                      />
                      <input
                        type="text"
                        value={options.header.background_color}
                        onChange={(e) => {
                          const color = e.target.value;
                          if (/^#[0-9A-Fa-f]{0,6}$/.test(color) || color === '') {
                            handleNestedOptionChange('header', 'background_color', color);
                          }
                        }}
                        placeholder="#ffffff"
                        maxLength={7}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>헤더 텍스트 색상</label>
                    <div className="color-input-container">
                      <input
                        type="color"
                        value={options.header.text_color}
                        onChange={(e) => handleNestedOptionChange('header', 'text_color', e.target.value)}
                      />
                      <input
                        type="text"
                        value={options.header.text_color}
                        onChange={(e) => {
                          const color = e.target.value;
                          if (/^#[0-9A-Fa-f]{0,6}$/.test(color) || color === '') {
                            handleNestedOptionChange('header', 'text_color', color);
                          }
                        }}
                        placeholder="#333333"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* 템플릿 탭 */}
          {activeTab === 'template' && (
            <div className="options-section template-editor-section">
              <div className="template-editor-header">
                <p className="template-editor-info">
                  대시보드 템플릿을 JSON 형식으로 직접 편집할 수 있습니다.
                </p>
                <button 
                  className="format-button"
                  onClick={() => {
                    try {
                      const parsed = JSON.parse(editedTemplate);
                      setEditedTemplate(JSON.stringify(parsed, null, 2));
                      setTemplateError(null);
                    } catch (e) {
                      setTemplateError('JSON 형식이 올바르지 않습니다: ' + e.message);
                    }
                  }}
                >
                  포맷 정리
                </button>
              </div>
              
              {templateError && (
                <div className="template-error-message">
                  {templateError}
                </div>
              )}
              
              <div className="template-editor-container">
                <Suspense fallback={<div className="editor-loading">에디터 로딩 중...</div>}>
                  <Editor
                    height="400px"
                    defaultLanguage="json"
                    value={editedTemplate}
                    onChange={(value) => {
                      setEditedTemplate(value || '');
                      // JSON 유효성 검사
                      try {
                        JSON.parse(value || '{}');
                        setTemplateError(null);
                      } catch (e) {
                        setTemplateError('JSON 형식 오류: ' + e.message);
                      }
                    }}
                    theme="vs-dark"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 14,
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                      formatOnPaste: true,
                      formatOnType: true
                    }}
                    onMount={(editor) => {
                      editorRef.current = editor;
                    }}
                  />
                </Suspense>
              </div>
            </div>
          )}
        </div>

        <div className="dashboard-options-editor-footer">
          <button className="cancel-button" onClick={onClose}>취소</button>
          <button className="save-button" onClick={handleApply}>적용</button>
        </div>
        </div>
      </div>
    </Modal>
  );
};

export default DashboardOptionsEditor; 